// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Content = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TDocumentDefinitions = any;

export interface SummaryData {
  name: string;
  sections: { title: string; rows: [string, string][] }[];
}

const TEAL = '#032f40';
const BURGUNDY = '#791d1d';

export async function buildPdf(s: SummaryData): Promise<Blob> {
  const pdfMakeMod = await import('pdfmake/build/pdfmake');
  const fontsMod = await import('pdfmake/build/vfs_fonts');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pdfMake: any = (pdfMakeMod as any).default ?? pdfMakeMod;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fonts: any = (fontsMod as any).default ?? fontsMod;
  pdfMake.vfs = fonts?.pdfMake?.vfs ?? fonts?.vfs ?? fonts;

  const body: Content[] = [];
  s.sections.forEach((sec) => {
    body.push({ text: sec.title.toUpperCase(), style: 'section', margin: [0, 14, 0, 6] });
    body.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#c8c8c8' }], margin: [0, 0, 0, 8] });
    if (sec.title === 'Summary') {
      body.push({ text: sec.rows[0]?.[1] ?? '', fontSize: 10, lineHeight: 1.35 });
      return;
    }
    const pairs: Content[][] = [];
    for (let i = 0; i < sec.rows.length; i += 2) {
      const cell = (r?: [string, string]): Content => (r ? { stack: [{ text: r[0].toUpperCase(), style: 'label' }, { text: r[1], fontSize: 10 }], margin: [0, 0, 0, 8] } : { text: '' });
      pairs.push([cell(sec.rows[i]), cell(sec.rows[i + 1])]);
    }
    body.push({ table: { widths: ['50%', '50%'], body: pairs }, layout: 'noBorders' });
  });

  const doc: TDocumentDefinitions = {
    pageMargins: [40, 110, 40, 50],
    background: () => ({ canvas: [{ type: 'rect', x: 0, y: 0, w: 595.28, h: 84, color: TEAL }] }),
    header: () => ({
      margin: [40, 24, 40, 0],
      stack: [
        { text: 'Sport Decisions Library', color: 'white', fontSize: 15, bold: true },
        { text: 'Adjudicative Decision Summary — CONFIDENTIAL', color: 'white', fontSize: 10, margin: [0, 6, 0, 0] },
      ],
    }),
    footer: (page: number, count: number) => ({
      margin: [40, 10, 40, 0],
      columns: [
        { text: `Generated: ${new Date().toLocaleString('en-CA')}`, fontSize: 8, color: '#828282' },
        { text: 'CONFIDENTIAL', fontSize: 8, bold: true, color: BURGUNDY, alignment: 'center' },
        { text: `Sport Decisions Library · ${page}/${count}`, fontSize: 8, color: '#828282', alignment: 'right' },
      ],
    }),
    content: [{ text: 'CASE REFERENCE NO.', style: 'label' }, { text: s.name, fontSize: 14, bold: true, color: TEAL }, ...body],
    styles: {
      label: { fontSize: 7.5, bold: true, color: '#646464', margin: [0, 0, 0, 2] },
      section: { fontSize: 9, bold: true, color: TEAL, characterSpacing: 1 },
    },
    defaultStyle: { color: '#1e1e1e' },
  };
  return new Promise((resolve, reject) => {
    try {
      const out = pdfMake.createPdf(doc);
      const r = out.getBlob((b: Blob) => resolve(b));
      if (r && typeof r.then === 'function') r.then(resolve, reject);
    } catch (e) {
      reject(e);
    }
  });
}

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function buildWordHtml(s: SummaryData): string {
  const secs = s.sections.map((sec) => `
    <h2 style="font-size:10pt;color:${TEAL};letter-spacing:1px;border-bottom:1px solid #ccc;padding-bottom:3px;margin-top:16pt">${esc(sec.title.toUpperCase())}</h2>
    ${sec.title === 'Summary' ? `<p style="font-size:10.5pt;line-height:1.4">${esc(sec.rows[0]?.[1] ?? '')}</p>` : `<table style="width:100%;border-collapse:collapse">${sec.rows.map(([k, v]) => `<tr><td style="width:38%;font-size:8pt;font-weight:bold;color:#646464;padding:3pt 0;vertical-align:top">${esc(k.toUpperCase())}</td><td style="font-size:10.5pt;padding:3pt 0">${esc(v)}</td></tr>`).join('')}</table>`}`).join('');
  return `<html><head><meta charset="utf-8"><title>${esc(s.name)}</title></head><body style="font-family:Calibri,Arial,sans-serif">
    <div style="background:${TEAL};color:white;padding:14pt"><p style="font-size:15pt;font-weight:bold;margin:0">Sport Decisions Library</p><p style="font-size:10pt;margin:4pt 0 0">Adjudicative Decision Summary — CONFIDENTIAL</p></div>
    <p style="font-size:8pt;font-weight:bold;color:#646464;margin-top:14pt">CASE REFERENCE NO.</p><p style="font-size:14pt;font-weight:bold;color:${TEAL};margin:0">${esc(s.name)}</p>
    ${secs}
    <p style="margin-top:24pt;font-size:8pt;color:#828282">Generated ${new Date().toLocaleString('en-CA')} · <b style="color:${BURGUNDY}">CONFIDENTIAL</b> · Sport Decisions Library</p>
  </body></html>`;
}

export function downloadBlob(blob: Blob, filename: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename.replace(/[^a-z0-9._-]+/gi, '-');
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}
